import { colors } from "@mui/material";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { GridRenderCellParams } from "@mui/x-data-grid";

import RowFlag from "@components/Flagging/RowFlag";
import IndustryChip from "@components/IndustryChip/IndustryChip";
import RowActionsMenu from "@components/RowActionsMenu";
import ColumnHeader from "@components/Table/ColumnHeader";
import WebsiteLinkWithFallback from "@components/WebsiteLinkWithFallback/WebsiteLinkWithFallback";

import { ICompanyTableControls } from "@store/filters/filter.types";

interface IColumnConfig {
  addIndustryToFilter: (industry: string) => void;
  filtersObject: ICompanyTableControls;
  preferredIndustries?: string[];
  rowMenuItems?: { func: (companyId: string) => void; label: string }[];
}

const generateCompanyColumns = ({
  addIndustryToFilter,
  filtersObject,
  preferredIndustries,
  rowMenuItems,
}: IColumnConfig) => {
  const columns = [
    {
      field: "flag",
      renderCell: (parameters: GridRenderCellParams) => (
        <RowFlag form="company" rowParams={parameters} workflow="smart-search" />
      ),
      renderHeader: () => <ColumnHeader title="" tooltip="" />,
      resizable: false,
      sortable: false,
      width: 60,
    },
    {
      field: "logo",
      renderCell: (parameters: GridRenderCellParams) => (
        <Box
          alt={`${parameters.value.alt} logo`}
          component="img"
          onError={(event) => {
            event.currentTarget.style.display = "none";
          }}
          src={parameters.value.url}
          sx={{ maxHeight: "51px", maxWidth: "51px", width: "auto" }}
        />
      ),
      renderHeader: () => <ColumnHeader title="" tooltip="" />,
      resizable: false,
      sortable: false,
      width: 71,
    },
    {
      field: "name",
      flex: 1,
      minWidth: 300,
      renderCell: (parameters: GridRenderCellParams) => (
        <Typography
          sx={{
            color: colors.blue[600],
            cursor: "pointer",
            fontWeight: 600,
            textDecoration: "underline",
          }}
          variant="subtitle2"
        >
          {parameters.value}
        </Typography>
      ),
      renderHeader: () => <ColumnHeader title="Company" tooltip="Recommended companies based on your search." />,
      resizable: false,
    },
    {
      field: "primaryDomain",
      renderCell: (parameters: GridRenderCellParams) =>
        parameters.value === "-" ? (
          <Typography variant="subtitle2">{parameters.value}</Typography>
        ) : (
          <WebsiteLinkWithFallback
            company={parameters.row.name}
            websiteNakedDomain={parameters.value}
            workflow="smart-search"
          />
        ),
      renderHeader: () => <ColumnHeader title="Website" tooltip="" />,
      resizable: false,
      sortable: false,
      width: 90,
    },
    {
      field: "numEmployees",
      renderCell: (parameters: GridRenderCellParams) => (
        <Typography variant="subtitle2">{parameters.value ? parameters.value.toLocaleString() : "-"}</Typography>
      ),
      renderHeader: () => <ColumnHeader title="# Employees" tooltip="Number of employees at this company." />,
      resizable: false,
      width: 200,
    },
    {
      field: "country",
      renderCell: (parameters: GridRenderCellParams) => <Typography variant="subtitle2">{parameters.value}</Typography>,
      renderHeader: () => <ColumnHeader title="Country" tooltip="Country of Headquarter's location." />,
      resizable: false,
      width: 200,
    },
    {
      field: "sector",
      flex: 1,
      minWidth: 1500,
      renderCell: (parameters: GridRenderCellParams) => (
        <Stack
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "nowrap",
          }}
        >
          {parameters.value.map((sector: string) => (
            <IndustryChip
              isPreferredIndustry={
                preferredIndustries &&
                preferredIndustries.some((preferred) => sector.toLowerCase().includes(preferred.toLowerCase()))
              }
              isSelected={filtersObject.filters.industries?.some((index) => index.toLowerCase() === sector)}
              key={sector}
              sector={sector}
              setIndustry={addIndustryToFilter}
            />
          ))}
        </Stack>
      ),
      renderHeader: () => <ColumnHeader title="Industry Sector(s)" tooltip="Sector(s) this company operates in." />,
      resizable: true,
      sortable: false,
    },
  ];

  if (rowMenuItems) {
    columns.splice(0, 0, {
      field: "rowActions",
      flex: 1,
      minWidth: 60,
      renderCell: (parameters: GridRenderCellParams) => {
        return (
          <RowActionsMenu
            rowMenuItems={rowMenuItems.map((menuItem) => {
              return {
                func: () => menuItem.func(parameters.id.toString()),
                label: menuItem.label,
              };
            })}
          />
        );
      },
      renderHeader: () => ColumnHeader({ title: "", tooltip: "" }),
      resizable: false,
      sortable: false,
    });
  }

  return columns;
};

export default generateCompanyColumns;
